import React, { useState } from 'react'
import { learningService, type OnboardingPayload } from '../../../services/learning'
import type { StarterTrack } from '../../../types/learning'

interface LearningOnboardingProps {
  starterTracks: StarterTrack[]
  defaultLanguage?: string
  onComplete: () => void
}

const GOAL_OPTIONS = [
  { value: 'school_support', label: 'School support' },
  { value: 'career_growth', label: 'Career growth' },
  { value: 'language_fluency', label: 'Language fluency' },
  { value: 'exam_prep', label: 'Exam preparation' },
  { value: 'personal_interest', label: 'Personal interest' },
]

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'ar', label: 'العربية' },
  { value: 'tr', label: 'Türkçe' },
]

// Learning-first onboarding: Choose goal → interests → language → recommended
// path → begin. Posts to /api/learning/onboarding, which captures preferences,
// generates an initial deterministic recommendation set, and marks onboarding
// complete.
export const LearningOnboarding: React.FC<LearningOnboardingProps> = ({ starterTracks, defaultLanguage = 'en', onComplete }) => {
  const [step, setStep] = useState(0)
  const [goals, setGoals] = useState<string[]>([])
  const [interests, setInterests] = useState<string[]>([])
  const [language, setLanguage] = useState(defaultLanguage)
  const [selectedTrack, setSelectedTrack] = useState<string | null>(starterTracks[0]?.skillTrack ?? null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const toggle = (list: string[], value: string, setter: (next: string[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value])
  }

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload: OnboardingPayload = {
        learningLanguage: language,
        goals,
        interests: selectedTrack ? Array.from(new Set([...interests, selectedTrack])) : interests,
      }
      await learningService.completeOnboarding(payload)
      onComplete()
    } catch (err) {
      setError('We could not save your preferences. Please try again.')
      setSaving(false)
    }
  }

  return (
    <section className="panel workspace-panel stack" data-testid="learning-onboarding">
      <div className="stack">
        <h2 className="heading-lg">Welcome to Learning</h2>
        <p className="text-muted">Let's set up your learning home. This takes about a minute.</p>
      </div>

      {step === 0 && (
        <div className="stack" data-testid="learning-onboarding-goals">
          <h3>What do you want to achieve?</h3>
          <div className="card-grid">
            {GOAL_OPTIONS.map((goal) => (
              <button
                key={goal.value}
                type="button"
                className={`btn ${goals.includes(goal.value) ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => toggle(goals, goal.value, setGoals)}
              >
                {goal.label}
              </button>
            ))}
          </div>
          <div className="button-row">
            <button type="button" className="btn btn-primary" onClick={() => setStep(1)} disabled={goals.length === 0}>Continue</button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="stack" data-testid="learning-onboarding-interests">
          <h3>Pick a learning track to start</h3>
          <p className="text-muted">You can add more later — this just sets your first recommendations.</p>
          <div className="card-grid">
            {starterTracks.map((track) => (
              <button
                key={track.id}
                type="button"
                className={`btn ${selectedTrack === track.skillTrack ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => {
                  setSelectedTrack(track.skillTrack)
                  toggle(interests, track.skillTrack, setInterests)
                }}
              >
                {track.name}
              </button>
            ))}
          </div>
          <div className="button-row">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(0)}>Back</button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>Continue</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="stack" data-testid="learning-onboarding-language">
          <h3>Which language do you want to learn in?</h3>
          <div className="card-grid">
            {LANGUAGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`btn ${language === option.value ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setLanguage(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {error && <p className="helper" role="alert">{error}</p>}
          <div className="button-row">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(1)}>Back</button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={saving} data-testid="learning-onboarding-finish">
              {saving ? 'Setting up…' : 'Begin learning'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
